import { FormTab, FormTabProps, required, useRecordContext, SelectInput } from 'react-admin';

import CraftPageSection from 'src/components/CraftPageSection';
import CraftTwoColumns from 'src/components/CraftTwoColumns';
import TextInput from 'src/inputs/TextInput';
import JsonWithRawOptionInput from 'src/inputs/JsonWithRawOptionInput';
import AutocompleteArrayInput from 'src/inputs/AutocompleteArrayInput';
import { CriterionRecord } from 'src/types';
import { RISK_SCAN_SOURCE_TYPES } from 'src/utils/defaults/Constants';
import tooltips from '../tooltips';

const DetailsTab = ({ mode, ...rest }: Omit<FormTabProps, 'label'> & { mode: 'create' | 'edit' }) => {
  const record = useRecordContext<CriterionRecord>();
  const currentSearchColumns = record?.search_columns ?? [];
  const currentApiFieldPaths = record?.api_field_paths ?? [];

  // NOTE: We currently reference Portal's codebase for the possible search columns values, so we
  // just initialize the options with the signal's current `search_columns` value.
  const searchColumnOptions = currentSearchColumns.map((column) => ({
    id: column,
    name: column,
  }));

  // NOTE: We currently reference Portal's codebase for the possible API field paths values, so we
  // just initialize the options with the criteria's current `api_field_paths` value.
  const apiFieldPathOptions = currentApiFieldPaths.map((path) => ({
    id: path,
    name: path,
  }));

  return (
    <FormTab label="Details" {...rest}>
      <CraftPageSection title="General">
        <CraftTwoColumns
          left={<TextInput source="name" validate={required()} autoFocus={mode === 'create'} />}
          right={<TextInput source="description" validate={required()} multiline />}
        />
        <CraftTwoColumns
          left={<JsonWithRawOptionInput label="Search filters" source="search_filters" isRequired />}
          right={
            <>
              <AutocompleteArrayInput
                label="Search columns"
                source="search_columns"
                infoTooltip={tooltips.search_columns}
                choices={searchColumnOptions}
                createItemLabel="Add %{item}"
                onCreate={(val) => {
                  if (!val) return null;
                  const item = { id: val, name: val };
                  searchColumnOptions.push(item);
                  return item;
                }}
              />
              <AutocompleteArrayInput
                label="API Field Paths"
                source="api_field_paths"
                infoTooltip={tooltips.api_field_paths}
                choices={apiFieldPathOptions}
                createItemLabel="Add %{item}"
                onCreate={(val) => {
                  if (!val) return null;
                  const item = { id: val, name: val };
                  apiFieldPathOptions.push(item);
                  return item;
                }}
              />
              <SelectInput label="Source" source="source" choices={RISK_SCAN_SOURCE_TYPES} validate={required()} />
              <JsonWithRawOptionInput label="Datalake expressions" source="datalake_expressions" />
            </>
          }
        />
      </CraftPageSection>
    </FormTab>
  );
};

export default DetailsTab;
